/*  ========================================================================
    # Sections - LeporelloDay
    ========================================================================  */

import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useLocalization from '../../hooks/useLocalization';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';
import ReactTooltip from 'react-tooltip';

const LeporelloDay = forwardRef(({
  index,
  day,
  hideInfoButtons,
  hideTicketButtons,
  skeleton,
  isCurrentDay,
  isOutdated,
  scrollToDay,
  periodReport,
  onKeyDown
}, ref) => {
  const dayRef = useRef(null);
  const dialogRef = useRef(null);
  const _ = useTranslation();
  const { xlUp, mdUp } = useBreakpoints();
  const lang = 'de-DE';
  const date = new Date(day.date);
  date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000); // fix US timezone
  const currentDate = new Date();
  const isCurrentYear = currentDate.getFullYear() === date.getFullYear();
  const [activeArrivalInfo, setActiveArrivalInfo] = useState([]);
  const [activeArrivalTitle, setActiveArrivalTitle] = useState(null);
  const arrivalInfoExists = day.items?.length > 0 ? day.items?.find((el) => { return el.arrivalInfo?.length > 0 }) : undefined;
  const formattedLongDate = `${date.toLocaleDateString(lang, { day: 'numeric' })} ${date.toLocaleDateString(lang, { month: 'long' })} ${date.toLocaleDateString(lang, { year: 'numeric' })}`

  // Icons
  const local = useLocalization();
  const iconCalendar = `${local('svgSprite')}#calendar`;
  const iconInfo = `${local('svgSprite')}#info`;
  const iconClose = `${local('svgSprite')}#close`;

  // Scroll to day
  useEffect(() => {
    if (scrollToDay) {
      dayRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [scrollToDay]);

  /**
   * Get ticket button.
   */
  const getTicketButton = (status, url, customText = null) => {
    if (url && (status === 'ticketing_default' || status === 'ticketing_placecards' || (status === 'ticketing_custom' && customText))) {
      return (
        <a href={url} aria-label={_(status)} className={'c-button'} target='_blank' rel='noreferrer'>
          {status === 'ticketing_custom' && customText ? customText : _(status)}
        </a>
      );
    } else if (
      status === 'ticketing_sold_out' ||
      status === 'ticketing_remaining' ||
      status === 'ticketing_theather_checkout' ||
      status === 'ticketing_school_performance' ||
      status === 'ticketing_guest_performance'
    ) {
      return (
        <button className={'c-button'} disabled={true} aria-label={_(status)}>
          {_(status)}
        </button>
      );
    } else if (status === 'ticketing_free') {
      return (
        <button className={'c-button'} disabled={true} aria-label={_(status)}>
          {_(status)}
        </button>
      );
    } else if (status === 'ticketing_no_performance') {
      return (
        <button className={'c-button u-text-red'} disabled={true} aria-label={_(status)}>
          {_(status)}
        </button>
      );
    } else if (status === 'ticketing_none') {
      return;
    }
  };

  /**
   * Get performance time.
   */
  const getPerformanceTime = (startTime, endTime) => {
    if (endTime) {
      return _('leporello_double_time', { startTime, endTime });
    } else {
      return _('leporello_single_time', { time: startTime });
    }
  };

  /**
   * Handle open dialog.
   */
  const handleOpenDialog = (arrivalInfos, arrivalModalTitle) => {
    if (dialogRef && dialogRef.current) {
      setActiveArrivalInfo(arrivalInfos);
      setActiveArrivalTitle(arrivalModalTitle)
      document.body.style.overflow = 'hidden';
      dialogRef.current.showModal();
    }
  }

  /**
   * Handle close dialog.
   */
  const handleCloseDialog = () => {
    if (dialogRef && dialogRef.current) {
      document.body.style.overflow = '';
      dialogRef.current.close();
    }
  }

  const getCalendarICSButton = (icsLink) => {
    return (
      <a
        href={icsLink}
        aria-label={_('leporello_add_to_calendar')}
        target='_blank'
        className='c-label'
        rel='noreferrer'
      >
        {_('leporello_add_to_calendar')}
        <svg viewBox='0 0 200 200'>
          <use href={iconCalendar} />
        </svg>
      </a>
    )
  }

  return (
    <>
      {skeleton ? (
        <div
          className='c-leporello-day c-leporello-day--skeleton'
          style={{ '--animation-delay': index }}
        >
          <div className='c-leporello-day__sidebar'>
            <div className='c-leporello-day__date'>
              <p className='c-leporello-day__date-number' />
              <p className='c-leporello-day__date-name' />
            </div>
          </div>
          <div className='c-leporello-day__performances'>
            <div className='c-leporello-day__performance'>
              <div className='c-leporello-day__performance-meta'>
                <p className='c-leporello-day__performance-time' />
                <p className='c-leporello-day__performance-location' />
              </div>
              <div className='c-leporello-day__performance-info'>
                <div className='c-leporello-day__performance-content'>
                  <div className='c-leporello-day__performance-title' />
                  <p className='c-leporello-day__performance-subtitle' />
                </div>
                <div className='c-button-group'>
                  <div />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`
          c-leporello-day
          ${isCurrentDay ? 'c-leporello-day--current' : ''}
          ${isOutdated ? 'c-leporello-day--outdated' : ''}
          `}
          id={
            date.toLocaleDateString(lang, { year: 'numeric' }) +
            date.toLocaleDateString(lang, { month: '2-digit' }) +
            date.toLocaleDateString(lang, { day: '2-digit' })
          }
          style={{ '--animation-delay': index }}
          ref={ref}
          tabIndex={0}
          onKeyDown={onKeyDown}
          data-empty={day.items.length === 0 ? 'true' : 'false'}
          aria-label={day.items.length > 0 ? _('leporello_day_label', { date: formattedLongDate, count: day.items.length }) : _('leporello_day_label_empty', { date: formattedLongDate })}
        >
          <div className='c-leporello-day__sidebar'>
            <div className='c-leporello-day__date'>
              <p className='c-leporello-day__date-name'>
                {xlUp
                  ? date.toLocaleDateString(lang, { weekday: 'short' })
                  : date.toLocaleDateString(lang, { weekday: 'short' }) + '.'}
              </p>
              <p className='c-leporello-day__date-number'>
                {date.toLocaleDateString(lang, { day: 'numeric' })}
              </p>
              <p className='c-leporello-day__date-month'>
                {isCurrentYear
                  ? date.toLocaleDateString(lang, { month: 'short' })
                  : date.toLocaleDateString(lang, { month: 'short', year: '2-digit' })}
              </p>
            </div>
          </div>
          <div className='c-leporello-day__performances'>
            {day.items.length > 0 ? (
              day.items.map((performance, index) => (
                <div
                  key={index}
                  className='c-leporello-day__performance'
                  tabIndex={0}
                  aria-label={_('leporello_performance_label', {
                    title: performance.title,
                    time: getPerformanceTime(performance.startTime, performance.endTime),
                    location: performance.location,
                    subtitle: performance.subtitle
                  })}
                >
                  <div className='c-leporello-day__performance-meta'>
                    <p className='c-leporello-day__performance-time h3'>
                      {getPerformanceTime(performance.startTime, performance.endTime)}
                    </p>
                    <p className='c-leporello-day__performance-location'>{performance.location}</p>

                    {getCalendarICSButton(performance.ics)}
                  </div>
                  <div className='c-leporello-day__performance-info'>
                    <div className='c-leporello-day__performance-content'>
                      <div className='c-leporello-day__performance-title'>
                        {hideInfoButtons || !performance.permalink ? (
                          <h3 dangerouslySetInnerHTML={{ __html: performance.title }}></h3>
                        ) : (
                          <h3>
                            <a href={performance.permalink} aria-label={performance.title} dangerouslySetInnerHTML={{ __html: performance.title }} tabIndex={-1}>
                            </a>
                          </h3>
                        )}
                        {performance.flags && performance.flags.length > 0
                          ? performance.flags.map((flag, index) =>
                            flag === 'flag_premiere' || flag === 'flag_matinee' ? (
                              <span
                                key={index}
                                className='c-label c-label--badge'
                                style={{ '--label-badge-background': performance.segment.color }}
                              >
                                {_(flag)}
                              </span>
                            ) : null
                          )
                          : null}
                      </div>
                      <p className='c-leporello-day__performance-subtitle'>
                        {performance.subtitle}
                      </p>
                      <div className='c-leporello-day__performance-flags'>
                        {performance.segment && performance.segment.url ? (
                          <a
                            className='c-label'
                            href={performance.segment.url}
                            aria-label={performance.segment.label}
                            style={{ '--label-color': performance.segment.color }}
                          >
                            {performance.segment.label}
                          </a>
                        ) : (
                          performance.segment && (
                            <p
                              className='c-label'
                              style={{ '--label-color': performance.segment.color }}
                            >
                              {performance.segment.label}
                            </p>
                          )
                        )}
                        {performance.flags && performance.flags.length > 0
                          ? performance.flags.map((flag, index) =>
                            flag !== 'flag_premiere' || flag !== 'flag_matinee' ? (
                              <p key={index}>{_(flag)}</p>
                            ) : null
                          )
                          : null}
                        {performance.priceGroup ? (
                          <p>{performance.priceGroup}</p>
                        ) : null}
                        {performance.additionalInfos ? (
                          <div
                            className='c-label'
                            style={{ '--label-color': '#000' }}
                            data-tip
                            data-for={'tooltip-' + day.date + '-' + index}
                          >
                            {_('leporello_additional_infos_title')}
                            <svg viewBox='0 0 200 200'>
                              <use href={iconInfo}></use>
                            </svg>
                            <ReactTooltip id={'tooltip-' + day.date + '-' + index} effect='solid'>
                              <p>{performance.additionalInfos}</p>
                            </ReactTooltip>
                          </div>
                        ) : null}
                        {performance.arrivalInfo && performance.arrivalInfo.length > 0 ?
                          <div
                            className='c-label'
                            style={{ '--label-color': '#000' }}
                          >
                            {
                              performance.hideArrivalTitle ? null :
                                performance.arrivalTitleOverride ? performance.arrivalTitleOverride : day.arrivalTitle}
                            {performance.arrivalInfo.map((info, index) => (
                              <button type='button' aria-label={info.overlayTitle} onClick={() => handleOpenDialog(performance.arrivalInfo, performance.arrivalModalTitle)} className="c-leporello-day__performance-arrival" key={index}>
                                <img src={info.icon} alt={info.overlayTitle} width={30} height={30} />
                              </button>
                            ))}
                          </div>
                          : null}
                      </div>
                      {!mdUp ? (
                        <p>
                          <span>
                            {getPerformanceTime(performance.startTime, performance.endTime)}
                          </span>
                          {performance.location && <span> - {performance.location}</span>}
                        </p>
                      ) : null}
                      {!mdUp ? (
                        <div>
                          {getCalendarICSButton(performance.ics)}
                        </div>
                      ) : null}

                    </div>
                    <div className={`c-button-group ${mdUp && 'c-button-group--split'}`}>
                      {performance.permalink && !hideInfoButtons ? (
                        <a
                          href={performance.permalink}
                          aria-label={_('infos')}
                          className='c-button c-button--outline'
                        >
                          {_('infos')}
                        </a>
                      ) : null}
                      {!hideTicketButtons &&
                        getTicketButton(performance.ticketingStatus, performance.ticketUrl, performance.ticketingButtonText)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className='c-leporello-day__performance-empty h3'>
                {periodReport ? periodReport : _('leporello_no_shows')}
              </div>
            )}
          </div>
          {arrivalInfoExists ?
            <dialog className='c-leporello-day__modal' ref={dialogRef}>
              <div className="c-leporello-day__modal-header">
                <p className='h3'>{activeArrivalTitle}</p>
                <button onClick={() => handleCloseDialog()} aria-label={_('leporello_close_modal')}>
                  <svg viewBox='0 0 24 24'>
                    <use href={iconClose} />
                  </svg>
                </button>
              </div>
              {activeArrivalInfo.map((info, index) => (
                <div className="c-leporello-day__modal-content" key={index}>
                  <div className="c-leporello-day__modal-content-title">
                    <img src={info.icon} alt={`${info.overlayTitle} Icon`} width={30} height={30} />
                    <div
                      className='c-label'
                      style={{ '--label-color': '#000' }}>
                      {info.overlayTitle}
                    </div>
                  </div>
                  <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: info.description }} />
                </div>
              ))}
            </dialog>
            : null}
        </div>
      )}
    </>
  );
});

LeporelloDay.propTypes = {
  hideInfoButtons: PropTypes.bool,
  hideTicketButtons: PropTypes.bool,
  index: PropTypes.number,
  isCurrentDay: PropTypes.bool,
  isOutdated: PropTypes.bool,
  day: PropTypes.object,
  skeleton: PropTypes.bool,
  scrollToDay: PropTypes.bool,
  periodReport: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onKeyDown: PropTypes.func
};

export default LeporelloDay;
